/**
 * Entry Page — sparkle generation, tile glow, animation-tier-aware.
 * Reads the user's animation preference from localStorage and adjusts
 * sparkle count / glow behaviour accordingly.
 */

import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';

// ── Counts per animation tier ──────────────────────────────

const SPARKLE_COUNTS: Record<AnimationTier, number> = {
  playful: 18,
  reduced: 8,
  none: 0,
};

// ── Module-level state ─────────────────────────────────────

let sparkleMovementTimers: ReturnType<typeof setTimeout>[] = [];
let tileGlowInterval: ReturnType<typeof setInterval> | null = null;

// ── Sparkle generation ─────────────────────────────────────

function generateSparkles(container: HTMLElement, count: number): void {
  container.querySelectorAll('.ep-sparkle').forEach(s => s.remove());
  if (count === 0) return;

  for (let i = 0; i < count; i++) {
    const sparkle = document.createElement('span');
    sparkle.className = 'ep-sparkle ep-sparkle--glow';

    const size = 8 + Math.random() * 16; // 8–24px
    sparkle.style.width = `${size}px`;
    sparkle.style.height = `${size}px`;

    const opacity = 0.5 + Math.random() * 0.45;
    sparkle.style.opacity = String(opacity);
    sparkle.style.setProperty('--ep-sparkle-opacity', String(opacity));

    // Stagger glow animations to avoid all pulsing in sync
    const durationBase = 2.4 + Math.random() * 2;
    sparkle.style.setProperty('--ep-sparkle-duration', `${durationBase.toFixed(1)}s`);
    sparkle.style.animationDelay = `${(Math.random() * durationBase).toFixed(2)}s`;

    // Some slightly elongated vertically
    if (Math.random() > 0.65) {
      sparkle.style.transform = `scaleY(${1 + Math.random() * 0.35})`;
    }

    sparkle.style.top = `${(Math.random() * 100).toFixed(1)}%`;
    sparkle.style.left = `${(Math.random() * 100).toFixed(1)}%`;

    container.appendChild(sparkle);
  }
}

// ── Sparkle movement (Playful only) ───────────────────────

function startSparkleMovement(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.ep-sparkle').forEach(el => {
    scheduleSparkleMove(el, container);
  });
}

function scheduleSparkleMove(el: HTMLElement, container: HTMLElement): void {
  const delay = 4000 + Math.random() * 5000; // 4–9s
  const timer = setTimeout(() => {
    if (getAnimationPref() !== 'playful') return;
    const tx = (Math.random() - 0.5) * 28; // ±14px
    const ty = (Math.random() - 0.5) * 28;
    el.style.transition = 'translate 1.4s ease-in-out';
    el.style.translate = `${tx}px ${ty}px`;
    setTimeout(() => {
      el.style.translate = '0 0';
      scheduleSparkleMove(el, container);
    }, 1400);
  }, delay);
  sparkleMovementTimers.push(timer);
}

function clearSparkleMovement(): void {
  sparkleMovementTimers.forEach(t => clearTimeout(t));
  sparkleMovementTimers = [];
}

// ── Tile glow (Reduced + Playful) ─────────────────────────

function startTileGlow(hero: HTMLElement, tier: AnimationTier): void {
  if (tier === 'none') return;

  const tiles = hero.querySelectorAll<HTMLElement>('.ep-tile');
  if (tiles.length === 0) return;

  const glowDuration  = tier === 'playful' ? 2500 : 3200;
  const pauseDuration = tier === 'playful' ? 3000 : 5000;

  function glowNext(): void {
    const idx  = Math.floor(Math.random() * tiles.length);
    const tile = tiles[idx];
    tile.classList.add('ep-tile-glow');
    setTimeout(() => tile.classList.remove('ep-tile-glow'), glowDuration);
  }

  glowNext();
  tileGlowInterval = setInterval(glowNext, glowDuration + pauseDuration);
}

function stopTileGlow(hero: HTMLElement): void {
  if (tileGlowInterval !== null) {
    clearInterval(tileGlowInterval);
    tileGlowInterval = null;
  }
  hero.querySelectorAll('.ep-tile-glow').forEach(t => t.classList.remove('ep-tile-glow'));
}

// ── Init ───────────────────────────────────────────────────

function initEntryPage(): void {
  const hero = document.querySelector<HTMLElement>('.entry-hero');
  if (!hero) return;

  const sparkleContainer = document.getElementById('epSparkles');
  if (!sparkleContainer) return;

  const tier = getAnimationPref();
  const count = SPARKLE_COUNTS[tier];

  generateSparkles(sparkleContainer, count);

  if (tier !== 'none') {
    startTileGlow(hero, tier);
    if (tier === 'playful') {
      startSparkleMovement(sparkleContainer);
    }
  }

  logger.info('Entry page initialized', { tier, sparkles: count });
}

// ── Cleanup on page unload ─────────────────────────────────

function cleanup(): void {
  clearSparkleMovement();
  const hero = document.querySelector<HTMLElement>('.entry-hero');
  if (hero) stopTileGlow(hero);
}

document.addEventListener('DOMContentLoaded', initEntryPage);
window.addEventListener('unload', cleanup);
